import { useI18n } from '../../i18n/I18nContext.js';
import { PriceInput } from './PriceInput.js';
import { StockInput } from './StockInput.js';
import { blankOption, optionsProblem, type OptionDraft } from './productOptionsDraft.js';

export interface ProductOptionsEditorProps {
  enabled: boolean;
  onEnabledChange(enabled: boolean): void;
  label: string;
  onLabelChange(label: string): void;
  /** Raw text; empty = no limit. */
  max: string;
  onMaxChange(max: string): void;
  /** The maximum field holds something that is not a whole number >= 1. */
  maxInvalid?: boolean;
  /** Raw text; empty = no limit. How many EXTRAS (cheeses) may be chosen. */
  addonsMax: string;
  onAddonsMaxChange(max: string): void;
  addonsMaxInvalid?: boolean;
  /** Mix price: raw text for the flavour count (empty = none), the price, and whether the pair is incomplete/invalid. */
  mixMin: string;
  onMixMinChange(value: string): void;
  mixPriceCents: number | null;
  onMixPriceChange(cents: number | null, valid: boolean): void;
  mixInvalid?: boolean;
  drafts: OptionDraft[];
  onDraftsChange(drafts: OptionDraft[]): void;
  disabled?: boolean;
}

/**
 * Options (flavours) of one product: each with its own price, cost and stock.
 * A mix of several is charged as the most expensive chosen option, which the
 * form says so the admin is never surprised by a price on the terminal.
 */
export function ProductOptionsEditor({
  enabled,
  onEnabledChange,
  label,
  onLabelChange,
  max,
  onMaxChange,
  maxInvalid = false,
  addonsMax,
  onAddonsMaxChange,
  addonsMaxInvalid = false,
  mixMin,
  onMixMinChange,
  mixPriceCents,
  onMixPriceChange,
  mixInvalid = false,
  drafts,
  onDraftsChange,
  disabled,
}: ProductOptionsEditorProps) {
  const { t } = useI18n();
  const problem = enabled ? optionsProblem(drafts) : null;

  const patch = (key: string, change: Partial<OptionDraft>) =>
    onDraftsChange(drafts.map((d) => (d.key === key ? { ...d, ...change } : d)));
  const move = (index: number, by: -1 | 1) => {
    const target = index + by;
    if (target < 0 || target >= drafts.length) return;
    const next = [...drafts];
    const [item] = next.splice(index, 1);
    if (item) next.splice(target, 0, item);
    onDraftsChange(next);
  };

  return (
    <fieldset className="product-options" disabled={disabled}>
      <legend>{t('products.options.title')}</legend>
      <label htmlFor="product-options-enabled" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          id="product-options-enabled"
          type="checkbox"
          checked={enabled}
          onChange={(e) => {
            onEnabledChange(e.target.checked);
            if (e.target.checked && drafts.length === 0) onDraftsChange([blankOption()]);
          }}
        />
        {t('products.options.enable')}
      </label>

      {enabled ? (
        <>
          <p className="field-hint">{t('products.options.mixHint')}</p>
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <label htmlFor="product-options-label" style={{ flex: 1 }}>
              {t('products.options.label')}
              <input
                id="product-options-label"
                value={label}
                placeholder={t('products.options.labelPlaceholder')}
                onChange={(e) => onLabelChange(e.target.value)}
              />
            </label>
            <label htmlFor="product-options-max" style={{ width: 150 }}>
              {t('products.options.max')}
              <input
                id="product-options-max"
                type="number"
                min={1}
                className="numeric"
                style={{ textAlign: 'right', width: '100%' }}
                value={max}
                placeholder={t('products.options.maxPlaceholder')}
                aria-invalid={maxInvalid}
                onChange={(e) => onMaxChange(e.target.value)}
              />
              {maxInvalid ? (
                <span className="field-error" role="alert">
                  {t('products.options.maxInvalid')}
                </span>
              ) : null}
            </label>
          </div>

          <ul className="product-options__list">
            {drafts.map((d, index) => {
              // Every control names ITS option, so a screen reader hears
              // «Quitar: Pollo», not ten identical «Quitar».
              const who = d.name.trim() || String(index + 1);
              return (
              <li key={d.key} className="product-options__item">
                <label htmlFor={`${d.key}-name`}>
                  {t('products.options.name')} {index + 1}
                  <input
                    id={`${d.key}-name`}
                    value={d.name}
                    onChange={(e) => patch(d.key, { name: e.target.value })}
                  />
                </label>
                <PriceInput
                  label={`${d.isAddon ? t('products.options.addonPrice') : t('products.price')} · ${who}`}
                  valueCents={d.priceCents}
                  onChange={(cents, valid) => patch(d.key, { priceCents: cents, priceValid: valid })}
                />
                {d.isAddon ? (
                  // An extra carries no cost and no stock: it is priced on top of the flavour.
                  <p className="field-hint product-options__addon-note">{t('products.options.addonNote')}</p>
                ) : (
                  <>
                    <PriceInput
                      label={`${t('products.cost')} · ${who}`}
                      valueCents={d.costCents}
                      // Blank is a legal "unknown" and reports valid; mistyped text reports
                      // invalid and must stop the save, never be sent as "unknown".
                      onChange={(cents, valid) => patch(d.key, { costCents: cents, costValid: valid })}
                    />
                    <StockInput
                      label={`${d.id ? t('products.stockAdjustLabel') : t('products.stockOpeningLabel')} · ${who}`}
                      valueQty={d.stockQty}
                      onChange={(qty, valid) => patch(d.key, { stockQty: qty, stockValid: valid })}
                    />
                  </>
                )}
                <label htmlFor={`${d.key}-group`}>
                  {t('products.options.group')} · {who}
                  <input
                    id={`${d.key}-group`}
                    value={d.groupName}
                    placeholder={t('products.options.groupPlaceholder')}
                    onChange={(e) => patch(d.key, { groupName: e.target.value })}
                  />
                </label>
                <label htmlFor={`${d.key}-addon`} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <input
                    id={`${d.key}-addon`}
                    type="checkbox"
                    checked={d.isAddon}
                    onChange={(e) => patch(d.key, { isAddon: e.target.checked })}
                  />
                  {t('products.options.addon')} · {who}
                </label>
                <div className="product-options__actions">
                  <label htmlFor={`${d.key}-active`} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <input
                      id={`${d.key}-active`}
                      type="checkbox"
                      checked={d.active}
                      onChange={(e) => patch(d.key, { active: e.target.checked })}
                    />
                    {t('common.active')} · {who}
                  </label>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => move(index, -1)} disabled={index === 0} aria-label={`${t('products.options.moveUp')}: ${who}`}>
                    ↑
                  </button>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => move(index, 1)} disabled={index === drafts.length - 1} aria-label={`${t('products.options.moveDown')}: ${who}`}>
                    ↓
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    aria-label={`${t('products.options.remove')}: ${who}`}
                    onClick={() => onDraftsChange(drafts.filter((x) => x.key !== d.key))}
                  >
                    {t('products.options.remove')}
                  </button>
                </div>
              </li>
              );
            })}
          </ul>

          {problem ? (
            <p className="field-error" role="alert">
              {t(`products.options.problem.${problem}`)}
            </p>
          ) : null}

          {drafts.some((d) => d.isAddon) ? (
            <label htmlFor="product-addons-max" style={{ maxWidth: 220 }}>
              {t('products.options.addonsMax')}
              <input
                id="product-addons-max"
                type="number"
                min={1}
                className="numeric"
                style={{ textAlign: 'right', width: '100%' }}
                value={addonsMax}
                placeholder={t('products.options.maxPlaceholder')}
                aria-invalid={addonsMaxInvalid}
                onChange={(ev) => onAddonsMaxChange(ev.target.value)}
              />
              {addonsMaxInvalid ? (
                <span className="field-error" role="alert">
                  {t('products.options.maxInvalid')}
                </span>
              ) : null}
            </label>
          ) : null}

          <fieldset className="product-options__mix" style={{ maxWidth: 360 }}>
            <legend>{t('products.options.mixLegend')}</legend>
            <label htmlFor="product-mix-min">
              {t('products.options.mixMin')}
              <input
                id="product-mix-min"
                type="number"
                min={2}
                className="numeric"
                style={{ textAlign: 'right', width: '100%' }}
                value={mixMin}
                placeholder={t('products.options.mixNone')}
                aria-invalid={mixInvalid}
                onChange={(ev) => onMixMinChange(ev.target.value)}
              />
            </label>
            <PriceInput label={t('products.options.mixPrice')} valueCents={mixPriceCents} onChange={onMixPriceChange} />
            {mixInvalid ? (
              <span className="field-error" role="alert">
                {t('products.options.mixInvalid')}
              </span>
            ) : null}
          </fieldset>

          <button type="button" className="btn btn--secondary btn--sm" onClick={() => onDraftsChange([...drafts, blankOption()])}>
            {t('products.options.add')}
          </button>
          <p className="field-hint">{t('products.options.priceDerived')}</p>
        </>
      ) : null}
    </fieldset>
  );
}
